import { useEffect, useMemo, useRef, useState } from 'react';
import { orderBy } from 'lodash';
import { useDataService } from '../../../../../../services/ApiDataService';
import {
    FIELD_TYPE,
    NO_TEST_OPTION,
    WIFI_NETWORKS_API,
    WIFI_TESTS_API,
    WIFI_TESTS_QUERY,
} from './DynamicConfigConstants';

/**
 * Check whether any field in the given sections array matches a specific type.
 */
const hasFieldType = (sections, type) =>
    sections.some((section) => section.fields.some((row) => row.some((field) => field?.type === type)));

/**
 * Fetches WiFi network profiles when the sections contain a 'wi-fi networks' field.
 * Returns { ssidList, isLoading }.
 */
export const useWifiNetworks = (sections) => {
    const dataService = useDataService();
    const [ssidList, setSsidList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const fetchedRef = useRef(false);

    const needsWifiNetworks = useMemo(() => hasFieldType(sections, FIELD_TYPE.WIFI_NETWORKS), [sections]);

    useEffect(() => {
        if (!needsWifiNetworks || fetchedRef.current) return;
        fetchedRef.current = true;
        setIsLoading(true);

        dataService
            .get(WIFI_NETWORKS_API)
            .then((data) => {
                const sorted = orderBy(data, [(w) => (w.profileName || '').toLowerCase()]);
                setSsidList(sorted);
            })
            .catch(() => {
                setSsidList([]);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [needsWifiNetworks, dataService]);

    return { ssidList, isLoading: needsWifiNetworks && isLoading };
};

/**
 * Fetches WiFi test list when the sections contain a 'wi-fi tests' field.
 * Prepends a "No Test" option at the beginning.
 * Returns { wifiTestList, isLoading }.
 */
export const useWifiTests = (sections) => {
    const dataService = useDataService();
    const [wifiTestList, setWifiTestList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const fetchedRef = useRef(false);

    const needsWifiTests = useMemo(() => hasFieldType(sections, FIELD_TYPE.WIFI_TESTS), [sections]);

    useEffect(() => {
        if (!needsWifiTests || fetchedRef.current) return;
        fetchedRef.current = true;
        setIsLoading(true);

        dataService
            .get(WIFI_TESTS_API, WIFI_TESTS_QUERY)
            .then((data) => {
                const sorted = orderBy(data, ['name'], ['asc']);
                setWifiTestList([NO_TEST_OPTION, ...sorted]);
            })
            .catch(() => {
                setWifiTestList([NO_TEST_OPTION]);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [needsWifiTests, dataService]);

    return { wifiTestList, isLoading: needsWifiTests && isLoading };
};
